import { z } from 'zod';

/**
 * Backups
 * =======
 * One run of an organisation's own data-and-files export: every tenant-scoped
 * table's rows (minus credentials — see `backup-models.ts`) plus every
 * uploaded file this organisation owns, as one or more downloadable `.zip`
 * parts. Runs as a background job; this module is the shape the admin screen
 * polls and lists against.
 */

export const BACKUP_STATUSES = ['PENDING', 'RUNNING', 'COMPLETE', 'FAILED'] as const;
export type BackupStatus = (typeof BACKUP_STATUSES)[number];

/**
 * `WHNCMS` is the original format: every tenant-scoped table as a CSV (one
 * file per table, header row first), plus every uploaded file — see
 * `docs/backup-and-restore.md` for the manual restore procedure this shape
 * is meant to support. `ZOHO` is the same download — split into verified
 * parts the same way, listed in the same admin panel — but its `data/`
 * entries are Zoho-Data-Backup-shaped per-module CSVs with Zoho's own column
 * labels instead of WHNCMS's raw column names, so the archive is also
 * importable through the Zoho import wizard, on this org or another one, or
 * into Zoho itself.
 */
export const BACKUP_FORMATS = ['WHNCMS', 'ZOHO'] as const;
export type BackupFormat = (typeof BACKUP_FORMATS)[number];

export const createBackupSchema = z.object({
  format: z.enum(BACKUP_FORMATS).default('WHNCMS'),
});
export type CreateBackupInput = z.infer<typeof createBackupSchema>;

export interface BackupPartSummary {
  partIndex: number;
  fileName: string;
  sizeBytes: number;
  checksum: string;
}

export interface BackupManifest {
  createdAt: string;
  /** Row count written per table, so a total can be shown without re-reading every part. */
  recordCounts: Record<string, number>;
  fileCount: number;
  parts: BackupPartSummary[];
}

export interface BackupSummary {
  id: string;
  format: BackupFormat;
  status: BackupStatus;
  progressPercent: number;
  progressPhase: string | null;
  error: string | null;
  partCount: number;
  totalSizeBytes: number;
  initiatedBy: { id: string; fullName: string } | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface BackupDetail extends BackupSummary {
  manifest: BackupManifest | null;
}

export const listBackupsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export type ListBackupsQuery = z.infer<typeof listBackupsQuerySchema>;
